'use client';

import * as React from 'react';
import { gsap } from 'gsap';
import CmsImage from '@/components/ui/CmsImage';
import { ImageType } from '@/types/product';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiOriginalMediaUrl, resolveStrapiMediaUrl } from '@/lib/strapi-media-url';

interface ProductsHeroProps {
  title: string;
  subtitle: string;
  description: string;
  /** CMS multi-media field — typically a primary video plus one or two
   *  background images. Each entry becomes a hero slide. */
  bannerMedia?: ImageType[];
  bannerMediaPoster?: ImageType;
}

type HeroMediaSlide = {
  id: string;
  type: 'image' | 'video';
  src: string;
  poster?: string;
  mime?: string;
  alt: string;
  media?: ImageType;
};

const SLIDE_INTERVAL_MS = 7000;

export default function ProductsHero({
  title,
  subtitle: _subtitle,
  description: _description,
  bannerMedia,
  bannerMediaPoster,
}: ProductsHeroProps) {
  const posterUrl = getStrapiOriginalMediaUrl(bannerMediaPoster) ?? MEDIA_PLACEHOLDER;

  const resolvedSlides = React.useMemo<HeroMediaSlide[]>(() => {
    return (bannerMedia ?? [])
      .filter((item): item is ImageType => Boolean(item?.url))
      .map((item, index): HeroMediaSlide | null => {
        const isVideo = Boolean(item.mime?.startsWith('video/'));
        const src = isVideo ? resolveStrapiMediaUrl(item.url) : getStrapiOriginalMediaUrl(item);
        if (!src) return null;
        return {
          id: `cms-${item.id ?? index}`,
          type: isVideo ? 'video' : 'image',
          src,
          poster: isVideo ? posterUrl : undefined,
          mime: item.mime,
          alt: item.alternativeText || title || 'Products media',
          media: item,
        };
      })
      .filter((slide): slide is HeroMediaSlide => slide !== null);
  }, [bannerMedia, posterUrl, title]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const slideRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  React.useEffect(() => {
    if (activeIndex >= resolvedSlides.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, resolvedSlides.length]);

  React.useEffect(() => {
    if (resolvedSlides.length <= 1) return;
    const timer = window.setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % resolvedSlides.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, resolvedSlides.length]);

  React.useEffect(() => {
    const slides = slideRefs.current.filter(Boolean) as HTMLDivElement[];
    if (!slides.length) return;
    gsap.killTweensOf(slides);
    gsap.to(slides, { opacity: 0, duration: 0.65, ease: 'sine.inOut' });
    gsap.to(slideRefs.current[activeIndex], {
      opacity: 1,
      duration: 0.9,
      delay: 0.05,
      ease: 'sine.inOut',
    });
  }, [activeIndex]);

  return (
    <section className="relative aspect-[4/5] w-full overflow-hidden bg-black sm:aspect-[16/10] md:aspect-[16/9] lg:min-h-[100dvh] lg:aspect-auto">
      <div className="absolute inset-0">
        {resolvedSlides.length === 0 ? (
          <CmsImage
            src={MEDIA_PLACEHOLDER}
            alt={title || 'Products'}
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
        ) : (
          resolvedSlides.map((slide, index) => (
            <div
              key={slide.id}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
              className={`absolute inset-0 ${index === 0 ? 'opacity-100' : 'opacity-0'}`}
            >
              <CmsImage
                src={slide.src}
                media={slide.type === 'video' ? (slide.media as never) : null}
                posterSrc={slide.poster || MEDIA_PLACEHOLDER}
                alt={slide.alt}
                fill
                priority={index === 0}
                loading={index === 0 ? 'eager' : 'lazy'}
                className="object-cover"
                sizes="(min-width: 1920px) 1920px, 100vw"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            </div>
          ))
        )}
      </div>

      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 sm:bottom-6 lg:bottom-10">
        {resolvedSlides.length > 1
          ? resolvedSlides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to products slide ${index + 1}`}
                aria-current={activeIndex === index ? 'true' : undefined}
                className="flex min-h-11 min-w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              >
                <span
                  className={`relative block h-[3px] overflow-hidden rounded-full transition-all ${
                    activeIndex === index ? 'w-9 bg-white/35' : 'w-6 bg-white/35'
                  }`}
                >
                  {activeIndex === index ? (
                    <span
                      className="absolute inset-y-0 left-0 w-full origin-left bg-white"
                      style={{
                        animation: `productsHeroDotProgress ${SLIDE_INTERVAL_MS}ms linear forwards`,
                      }}
                    />
                  ) : null}
                </span>
              </button>
            ))
          : null}
      </div>

      <style jsx>{`
        @keyframes productsHeroDotProgress {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }
      `}</style>
    </section>
  );
}
